import React, { ChangeEvent, ExoticComponent, Fragment, ReactNode } from 'react';
import { Field, Form, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';
import Alert from 'react-bootstrap/Alert';
import { TextField } from '../components/fields';
import SubmitButton from '../components/submit-button';
import ErrorMessage from '../components/error-message';
import Tooltip from '../components/tooltip';
import { CounterpartyPermissionLevel, COUNTERPARTY_PERMISSION_LABELS } from '../types/counterparty-permissions';

interface InviteReceivingTeamFormProps {
  bodyContainer: ExoticComponent<{ children?: ReactNode }>;
  actionsContainer: ExoticComponent<{ children?: ReactNode }>;
  renderSecondaryAction: any;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  isValid: boolean;
  shouldDocumentBeShared: boolean;
  shouldAccessBeGranted?: boolean;
  isImportedPdf?: boolean;
  onCounterpartyPermissionChange?: (level: CounterpartyPermissionLevel) => void;
}

const InviteReceivingTeamForm = ({
  bodyContainer: BodyContainer = Fragment,
  actionsContainer: ActionsContainer = Fragment,
  renderSecondaryAction,
  errors,
  touched,
  isSubmitting,
  isValid,
  shouldDocumentBeShared,
  shouldAccessBeGranted,
  isImportedPdf,
  onCounterpartyPermissionChange,
}: InviteReceivingTeamFormProps) => {
  const { setFieldValue } = useFormikContext();
  return (
    <Form noValidate>
      <div>
        {!shouldAccessBeGranted ? (
          <BodyContainer>
            {shouldDocumentBeShared ? (
              <div>
                Send both the document and deal by inviting the primary contact from the counterparty. Your team will
                not be permitted to edit the document until the counterparty sends it back.
              </div>
            ) : (
              <div>Invite the primary contact from the counterparty to access the deal.</div>
            )}
          </BodyContainer>
        ) : (
          <BodyContainer>
            {shouldDocumentBeShared ? (
              <div>
                Send both the document and deal by inviting the primary contact from the counterparty. Both teams will
                be able to access the document.
              </div>
            ) : (
              <div>Both teams will be able to access the document.</div>
            )}
          </BodyContainer>
        )}

        <BodyContainer>
          {errors._general && (
            <Alert variant="danger">
              <ErrorMessage>{errors._general}</ErrorMessage>
            </Alert>
          )}

          <div style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
            <TextField
              id="inviteReceivingTeamForm.email"
              name="email"
              type="email"
              label={<FormattedMessage id="inviteReceivingTeamForm.email" defaultMessage="Email" />}
            />
            {touched.email && errors.email && (
              <div className="text-danger small mt-1">{errors.email}</div>
            )}
            <div style={{ marginTop: '1rem' }}>
              <Field
                className="invite-message-textarea"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                }}
                id="inviteReceivingTeamForm.additionalMessage"
                name="additionalMessage"
                component="textarea"
                placeholder="Message (Optional)"
              />
            </div>

            {/* Unified Permission Dropdown - Only show for imported PDFs when sharing document */}
            {isImportedPdf && shouldDocumentBeShared && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                    <span>Counterparty Permissions</span>
                    <Tooltip testId="counterparty-permission-tooltip-invite" placement="top">
                      Set what the counterparty can do with this document
                    </Tooltip>
                  </label>
                  <Field
                    as="select"
                    name="counterpartyPermissionLevel"
                    style={{ padding: '0.375rem 0.75rem', borderRadius: '4px', border: '1px solid #ced4da', minWidth: '200px' }}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      const newValue = e.target.value as CounterpartyPermissionLevel;
                      setFieldValue('counterpartyPermissionLevel', newValue);
                      // Immediately persist via mutation
                      onCounterpartyPermissionChange?.(newValue);
                    }}
                  >
                    {Object.entries(COUNTERPARTY_PERMISSION_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>
            )}
          </div>
        </BodyContainer>
        <ActionsContainer>
          {renderSecondaryAction?.()}
          <SubmitButton
            label={
              <FormattedMessage id="cta.send-deal" defaultMessage={shouldDocumentBeShared ? 'Send' : 'Send Deal'} />
            }
            disabled={isSubmitting || !isValid}
            isSubmitting={isSubmitting}
          />
        </ActionsContainer>
      </div>
    </Form>
  );
};

export default InviteReceivingTeamForm;
